import {
  Badge,
  Box,
  Button,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import { HiArrowLeft } from "react-icons/hi";
import {
  collectionAPI,
  formatDuration,
  formatFileSize,
  getVideoStatusColorScheme,
  getVideoStatusText,
  videoAPI,
} from "../../utils/bunnyApi";

const BunnyVideoSelector = ({
  isOpen,
  onClose,
  onSelect,
  title = "Select Video (Bunny)",
  verticalOnly = false,
}) => {
  const [view, setView] = useState("collections");
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [collections, setCollections] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetNavigation = useCallback(() => {
    setSelectedCollection(null);
    setSearchTerm("");
    setView("collections");
    setCollections([]);
    setVideos([]);
    setError(null);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetNavigation();
    }
  }, [isOpen, resetNavigation]);

  useEffect(() => {
    if (!isOpen || view !== "collections") return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    collectionAPI
      .getAll(1, 100, searchTerm || undefined)
      .then((data) => {
        if (!cancelled) setCollections(data?.items || []);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e?.message || "Failed to load Bunny collections");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, view, searchTerm]);

  useEffect(() => {
    if (!isOpen || view !== "videos" || !selectedCollection?.guid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    videoAPI
      .getAll(1, 100, searchTerm || undefined, selectedCollection.guid)
      .then((data) => {
        if (cancelled) return;
        const items = data?.items || [];
        const filtered = verticalOnly
          ? items.filter(
              (v) =>
                v.height != null &&
                v.width != null &&
                v.height > v.width
            )
          : items;
        setVideos(filtered);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e?.message || "Failed to load Bunny videos");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, view, searchTerm, selectedCollection, verticalOnly]);

  const handleSelectCollection = (collection) => {
    setSelectedCollection(collection);
    setSearchTerm("");
    setView("videos");
  };

  const handleBackToCollections = () => {
    setSelectedCollection(null);
    setSearchTerm("");
    setView("collections");
  };

  const handleSelectVideo = (video) => {
    onSelect(video);
    onClose();
    resetNavigation();
  };

  const handleModalClose = () => {
    resetNavigation();
    onClose();
  };

  const emptyMessage =
    view === "collections"
      ? "No collections found"
      : verticalOnly
        ? "No vertical videos found in this collection"
        : "No videos found in this collection";

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="800px">
        <ModalHeader>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            {view === "videos" ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<HiArrowLeft />}
                  onClick={handleBackToCollections}
                >
                  Back
                </Button>
                <Text fontWeight="normal" color="gray.500" fontSize="md">
                  {selectedCollection?.name}
                </Text>
              </>
            ) : (
              title
            )}
          </Box>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Input
            placeholder={
              view === "collections"
                ? "Search collections..."
                : "Search videos..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            mb={4}
          />
          {error && (
            <Text color="red.500" mb={2} fontSize="sm">
              {error}
            </Text>
          )}
          <Box maxH="400px" overflowY="auto">
            {loading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="lg" />
              </Box>
            ) : view === "collections" ? (
              collections.length > 0 ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  {collections.map((collection) => (
                    <Box
                      key={collection.guid}
                      onClick={() => handleSelectCollection(collection)}
                      display="flex"
                      alignItems="center"
                      gap={3}
                      p={3}
                      borderWidth="1px"
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: "gray.50" }}
                    >
                      <Text fontWeight="semibold" flex={1} minW={0}>
                        {collection.name}
                      </Text>
                      <Button size="sm" colorScheme="blue">
                        Open
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Text color="gray.500" textAlign="center" py={8}>
                  {emptyMessage}
                </Text>
              )
            ) : videos.length > 0 ? (
              <Box display="flex" flexDirection="column" gap={2}>
                {videos.map((video) => (
                  <Box
                    key={video.guid}
                    onClick={() => handleSelectVideo(video)}
                    display="flex"
                    alignItems="center"
                    gap={3}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                  >
                    <Image
                      src={videoAPI.getThumbnailUrl(video.guid)}
                      alt={video.title}
                      w="80px"
                      h="48px"
                      objectFit="cover"
                      borderRadius="md"
                      flexShrink={0}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <Box flex={1} minW={0}>
                      <Text fontWeight="semibold" noOfLines={2}>
                        {video.title}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatDuration(video.length || 0)} •{" "}
                        {formatFileSize(video.storageSize || 0)} •{" "}
                        {video.width}×{video.height}
                      </Text>
                    </Box>
                    <Badge
                      colorScheme={getVideoStatusColorScheme(video.status)}
                    >
                      {getVideoStatusText(video.status)}
                    </Badge>
                    <Button size="sm" colorScheme="blue" flexShrink={0}>
                      Select
                    </Button>
                  </Box>
                ))}
              </Box>
            ) : (
              <Text color="gray.500" textAlign="center" py={8}>
                {emptyMessage}
              </Text>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BunnyVideoSelector;
